import * as React from "react";
import { cx } from "../util.js";

export interface FieldProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Field label. */
  label?: React.ReactNode;
  /** Mark the field required (adds a red asterisk). */
  required?: boolean;
  /** Helper text shown below the control. */
  hint?: React.ReactNode;
  /** Error message; when set, the field renders in its invalid state. */
  error?: React.ReactNode;
  /** `id` of the control, wired to the label's `htmlFor`. */
  htmlFor?: string;
}
/** Label + control wrapper with hint/error handling. */
export function Field({ label, required, hint, error, htmlFor, className, children, ...rest }: FieldProps) {
  const generatedId = React.useId();
  const directControl = React.Children.toArray(children).find(child =>
    React.isValidElement(child) && ["input", "select", "textarea", Input, Select, Textarea].includes(child.type as typeof Input)
  ) as React.ReactElement<React.InputHTMLAttributes<HTMLInputElement>> | undefined;
  const controlId = htmlFor ?? directControl?.props.id ?? generatedId;
  const messageId = `${controlId}-message`;
  const controls = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return child;
    if (!["input", "select", "textarea", Input, Select, Textarea].includes(child.type as typeof Input)) return child;
    const control = child as React.ReactElement<React.InputHTMLAttributes<HTMLInputElement>>;
    if (control.props.id && control.props.id !== controlId) return child;
    return React.cloneElement(control, {
      id: controlId,
      required: required || control.props.required,
      "aria-invalid": error ? true : control.props["aria-invalid"],
      "aria-describedby": [control.props["aria-describedby"], error || hint ? messageId : null].filter(Boolean).join(" ") || undefined,
    });
  });
  return (
    <div className={cx("ip-field", className)} data-invalid={error ? "true" : undefined} {...rest}>
      {label && (
        <label className="ip-label" htmlFor={controlId}>
          {label}
          {required && <span className="ip-label-req" aria-hidden>*</span>}
        </label>
      )}
      {controls}
      {error ? (
        <p id={messageId} className="ip-hint" data-tone="error">{error}</p>
      ) : hint ? (
        <p id={messageId} className="ip-hint">{hint}</p>
      ) : null}
    </div>
  );
}

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {}
/** Single-line text input. */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, ...rest },
  ref
) {
  return <input ref={ref} className={cx("ip-input", className)} {...rest} />;
});

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {}
/** Multi-line text input. */
export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, rows = 3, ...rest },
  ref
) {
  return <textarea ref={ref} className={cx("ip-textarea", className)} rows={rows} {...rest} />;
});

export interface SelectOption {
  value: string;
  label: string;
}
export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  /** Convenience: render options from data. Children take precedence if provided. */
  options?: SelectOption[];
  /** Optional leading placeholder option. */
  placeholder?: string;
}
/**
 * Native select. Does not submit the form on Enter and commits selection on
 * keyboard navigation (avoids the legacy select pitfalls).
 */
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, options, placeholder, children, onKeyDown, ...rest },
  ref
) {
  return (
    <select
      ref={ref}
      className={cx("ip-select", className)}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.preventDefault(); // never submit the form from the select
        onKeyDown?.(e);
      }}
      {...rest}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {children ?? options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
});
